// src/utils/howToTopics.js
// ===========================================================================
// The "How do I…?" guide: where to add everything, step by step, with
// "Take me there" links that open the exact screen (utils/appLinks.js).
// Shown from the reminder popups (Home), Team Dashboard → Reminders, and
// Admin Portal → ⋯ More → How do I…?
//
// A topic:  { id, group, title, keywords, steps: [..], links: [{ label, to }], tip? }
//   to = { page: "admin" | "team" | "home", go, more, open, path, params }
// Edit freely - text only, no code changes needed elsewhere.
// ===========================================================================

export const HOW_TO_GROUPS = ["Money in", "Money out", "Jobs", "Office"];

export const HOW_TO_TOPICS = [
  /* ------------------------------------------------------------ Money in */
  {
    id: "customer-payment",
    group: "Money in",
    title: "Record a customer payment (check, ACH, Zelle)",
    keywords: "deposit check paid invoice payment received customer",
    steps: [
      "Admin Portal → Deposits → + Add Payment.",
      "Pick the Work Order # (or Invoice #), type the amount, date, check # and who paid.",
      "Save. The job's paid total updates and the invoice moves to Paid once it's covered.",
      "Or: Admin Portal → Approved Invoices → find the invoice → Invoice Paid.",
    ],
    links: [
      { label: "Add a payment in Deposits", to: { page: "admin", go: "deposits", open: "add-payment" } },
      { label: "Approved Invoices", to: { page: "admin", go: "approved" } },
    ],
  },
  {
    id: "split-customer-check",
    group: "Money in",
    title: "One customer check that pays several jobs / invoices",
    keywords: "split check multiple invoices one check customer",
    steps: [
      "Admin Portal → Job Estimate Spreadsheet.",
      "On any of that customer's job rows click + Split Check.",
      "Enter the check #, date and total, tick every WO it covers, then Equal Divide or type each amount.",
      "Save Split Check. Each WO shows Check Received for its share.",
      "From Approved Invoices: Invoice Paid → split it between invoices also works.",
    ],
    links: [{ label: "Job Estimate Spreadsheet", to: { page: "admin", go: "jobestimatespreadsheet" } }],
  },
  {
    id: "invoice-job",
    group: "Money in",
    title: "Invoice a finished job",
    keywords: "invoice completed mark job as completed approved invoices deposit progress final",
    steps: [
      "Mark the job completed (Home or Team Dashboard). The popup asks how to invoice it:",
      "Standard = the full job total. Deposit / Progress / Final = part of a bigger job; it tracks what's left.",
      "The Draft lands in Admin Portal → Approved Invoices. Review it, then send it.",
      "Chose Decide later? It waits in Approved Invoices → Completed jobs waiting for an invoice.",
    ],
    links: [{ label: "Approved Invoices", to: { page: "admin", go: "approved" } }],
  },
  {
    id: "no-payment",
    group: "Money in",
    title: "Proposal / meeting / inspection / warranty - nothing to charge",
    keywords: "no payment warranty inspection proposal meeting free zero tech pay",
    steps: [
      "Mark the job completed.",
      "In the popup click the red No Payment Needed, then pick the reason (Proposal, Meeting, Inspection…).",
      "Already has an invoice in Approved Invoices? Click No Payment on that invoice's row and pick the reason.",
      "Either way it becomes a $0.00 invoice marked Paid. Techs assigned to the job are still paid, and material stays on the job as a cost.",
      "Picked it by mistake? Paid Invoices → Undo No Payment puts the amount back.",
    ],
    links: [{ label: "Approved Invoices → Paid", to: { page: "admin", go: "approved" } }],
  },
  {
    id: "bill-later",
    group: "Money in",
    title: "Job still going on the next WO - bill it later",
    keywords: "bill later continue continuation next wo still going roll over balance no payment now",
    steps: [
      "Click Continue on the job. The old WO completes and a new one (e.g. 5456-2) is scheduled.",
      "In the invoice popup click the red No Payment Needed, then the blue Bill Later – continues on WO 5456-2.",
      "No invoice is made for the old WO. The job total rolls over to the new WO and is billed there when the work is done.",
      "If the new WO is ever deleted, the old one comes back in Approved Invoices → Completed jobs waiting for an invoice.",
    ],
    links: [{ label: "Approved Invoices", to: { page: "admin", go: "approved" } }],
  },
  {
    id: "contract-billing",
    group: "Money in",
    title: "Monthly / recurring contract billing",
    keywords: "monthly contract recurring billing invoice every month",
    steps: [
      "Admin Portal → Contract Billing → Set up on the contract (price, how often, bill from).",
      "Every period: Due now → tick → Create Draft invoices (makes a WO + a Draft invoice).",
      "Already invoiced a month another way? Set up → Periods → type its Invoice # or WO.",
      "Use the ? How this page works button there for the full walkthrough.",
    ],
    links: [{ label: "Contract Billing", to: { page: "admin", go: "contract-billing" } }],
  },

  /* ----------------------------------------------------------- Money out */
  {
    id: "set-sub-pay",
    group: "Money out",
    title: "Set how a sub contractor is paid (Daily or Project)",
    keywords: "sub contractor pay daily project rate 350 tech assign",
    steps: [
      "Open the job → Edit → Tech Assign: add the sub. A Sub Contractor pay box appears under it.",
      "Daily = workers × $ per day × days on the job. Project = one price for the whole job (owed once, even when continued).",
      "Save the job. The owed amount shows in the job's Sub Pay.",
      "Old jobs: Team Dashboard → Sub Pay Setup lists every job with a sub; filter Needs pay set.",
      "A sub on several daily jobs the same day gets ONE day's pay split across them automatically.",
    ],
    links: [
      { label: "Sub Pay Setup", to: { page: "team", go: "sub-pay-setup" } },
      { label: "Team Dashboard jobs", to: { page: "team", go: "job-history" } },
    ],
  },
  {
    id: "pay-sub-one-job",
    group: "Money out",
    title: "Pay a sub for one job",
    keywords: "pay sub contractor payment check one job record",
    steps: [
      "Open the job → Sub Pay → + Record payment (amount, date, how, check #).",
      "Or: Admin Portal → ⋯ More → Contractor Pay → Record a payment → Apply to a job.",
      "The job's Sub Pay shows paid and balance; Contractor Pay shows the sub's total.",
    ],
    links: [{ label: "Record a payment in Contractor Pay", to: { page: "admin", more: "contractorpay", open: "record-payment" } }],
  },
  {
    id: "pay-sub-several-jobs",
    group: "Money out",
    title: "Pay a sub for several jobs with one check",
    keywords: "split sub payment one check several jobs days multiple",
    steps: [
      "Open Split Sub Payment (Job Estimate Spreadsheet or Contractor Pay).",
      "Pick the sub, enter the check #, date, how and the total.",
      "Click Oldest first (pays each job's balance, oldest to newest) or tick jobs and Equal divide - or type amounts.",
      "Left to assign must be $0.00 (or tick Put the rest on their overall balance). Save split payment.",
      "One payment per job is saved under the same check #.",
    ],
    links: [
      { label: "Split Sub Payment (Spreadsheet)", to: { page: "admin", go: "jobestimatespreadsheet", open: "split-sub-payment" } },
      { label: "Split Sub Payment (Contractor Pay)", to: { page: "admin", more: "contractorpay", open: "split-sub-payment" } },
    ],
  },
  {
    id: "sub-payroll",
    group: "Money out",
    title: "Pay a sub on a schedule (weekly payroll plan)",
    keywords: "payroll weekly repeat schedule sub plan",
    steps: [
      "Admin Portal → Job Estimate Spreadsheet → Show Subcontractor Payables.",
      "Record payment: pick the sub, amount, then Repeat (weekly, every 2 weeks…) and the first date.",
      "Each planned payment is saved; delete any that don't happen.",
    ],
    links: [{ label: "Subcontractor Payables", to: { page: "admin", go: "jobestimatespreadsheet", open: "sub-payables" } }],
  },
  {
    id: "sub-double-day",
    group: "Money out",
    title: "A sub shows $350 × 5 for jobs on the same day",
    keywords: "double paid daily split shared day 350 five jobs",
    steps: [
      "Team Dashboard → Sub Pay Setup → Split shared days.",
      "It shows each job's before → after and asks first. Payments already recorded aren't touched.",
    ],
    links: [{ label: "Sub Pay Setup", to: { page: "team", go: "sub-pay-setup" } }],
  },
  {
    id: "monthly-bills",
    group: "Money out",
    title: "Monthly bills (rent, insurance, phone, loans…)",
    keywords: "monthly bills expenses rent insurance recurring bank",
    steps: [
      "Admin Portal → Expenses → Monthly Bills.",
      "Bills found in your bank statements are suggested there: Add as monthly bill or Not a bill.",
      "Add one by hand from the same screen. The Cash Plan uses this list.",
      "Upload statements in Admin Portal → Bank Statements; its banner shows how many bills are waiting for review.",
    ],
    links: [
      { label: "Monthly Bills", to: { page: "admin", go: "expenses", params: { tab: "bills" } } },
      { label: "Bank Statements", to: { page: "admin", go: "bankstatemententries" } },
    ],
  },
  {
    id: "expense-to-job",
    group: "Money out",
    title: "Tie a bank expense (materials, receipt) to a job",
    keywords: "expense receipt materials link job bank statement",
    steps: [
      "Importing a statement: Admin Portal → Bank Statements → in the import rows use the Job Match column to pick the WO.",
      "One-off expense: Bank Statements → Show Manual Bank Statement Entry → Link to Job (optional).",
      "It then counts as that job's cost in Job Profit.",
    ],
    links: [{ label: "Bank Statements", to: { page: "admin", go: "bankstatemententries" } }],
  },

  /* ----------------------------------------------------------------- Jobs */
  {
    id: "link-jobs",
    group: "Jobs",
    title: "Link jobs together (same project, follow-up visits)",
    keywords: "link jobs continue chain wo -2 linked related",
    steps: [
      "Use Continue on the job (Home or Team Dashboard). It completes this visit and makes the next WO (3101 → 3101-2), linked.",
      "Jobs whose WO shares the same base number are linked automatically (3101, 3101-2, 3101-3).",
      "To put an existing job in a chain, edit its WO to the next number in that family (e.g. 3101-3).",
      "See a chain: open the job → Jobs Linked (N).",
      "Project pay is owed once on the first job; daily pay counts each job's own days.",
    ],
    links: [{ label: "Team Dashboard jobs", to: { page: "team", go: "job-history" } }],
  },
  {
    id: "assign-tech",
    group: "Jobs",
    title: "A job has no tech assigned",
    keywords: "no tech assign technician reminder",
    steps: [
      "Team Dashboard → open the job → Edit → Tech Assign → pick who did it → Save.",
      "Or fix it right in Team Dashboard → Reminders → Job has no tech.",
    ],
    links: [{ label: "Reminders", to: { page: "team", go: "job-reminders" } }],
  },
  {
    id: "clocked-in-no-job",
    group: "Jobs",
    title: "Someone clocked in but wasn't on a job",
    keywords: "clocked in not on a job clock time unassigned",
    steps: [
      "Admin Portal → Clocked In, Not on a Job (or Team Dashboard → Reminders).",
      "Pick the job they worked on, or dismiss it (shop work, training…).",
    ],
    links: [{ label: "Clocked In, Not on a Job", to: { page: "admin", go: "unassigned-clock-ins" } }],
  },
  {
    id: "missed-jobs",
    group: "Jobs",
    title: "Missed jobs (date passed, not completed)",
    keywords: "missed overdue reschedule it was done skip",
    steps: [
      "Home → red Missed banner, or Needs Scheduling → Missed.",
      "Tick jobs → Reschedule, Needs Scheduling, It was done (goes to Ready to Invoice) or Skip visit (recurring). Pick a reason.",
    ],
    links: [{ label: "Needs Scheduling → Missed", to: { path: "/NeedsScheduling", params: { tab: "missed" } } }],
  },

  /* --------------------------------------------------------------- Office */
  {
    id: "licenses",
    group: "Office",
    title: "Add a COI, license or certificate",
    keywords: "license coi insurance certificate ein expiration",
    steps: [
      "Admin Portal → Licenses → Add License. Fill the Licensee OR Company name; tick No expiration date for things like an EIN.",
      "Upload the PDF (or JPG / PNG).",
      "Expiration emails follow ⋯ More → Settings → License & Certificates → Email alerts.",
    ],
    links: [
      { label: "Add a license", to: { page: "admin", go: "licenses", open: "add-license" } },
      { label: "License settings & alerts", to: { page: "admin", more: "licensesettings" } },
    ],
  },
  {
    id: "attach-to-proposal",
    group: "Office",
    title: "Attach a COI / license to the end of a proposal",
    keywords: "attach pdf proposal coi license end",
    steps: [
      "Open the proposal → Details → Attached to the end of this proposal.",
      "Pick it from License & Certificates → Attach (or Upload PDF / image). Reorder with ▲ ▼.",
      "Click Print/Download so the saved / emailed PDF includes them.",
    ],
    links: [{ label: "Proposals (Team Dashboard)", to: { page: "team", go: "proposals" } }],
  },
  {
    id: "who-gets-emails",
    group: "Office",
    title: "Change who gets email notifications",
    keywords: "email notifications recipients alerts",
    steps: ["Admin Portal → ⋯ More → Notifications → open the section → tick people → Save."],
    links: [{ label: "Notifications", to: { page: "admin", more: "notificationrecipients" } }],
  },
];

/** The topic that answers each reminder type. */
export const REMINDER_TOPIC = {
  proposalJob: "assign-tech",
  dupWO: "",
  noTech: "assign-tech",
  clockedIn: "clocked-in-no-job",
  subPay: "set-sub-pay",
  missed: "missed-jobs",
};
